import { connect } from 'react-redux';
import { FieldArray } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import { useCallback } from 'react';

import { KNM_VIOLATIONS_LIST } from 'CorePackage/MainModule/routes/routes';
import { getViolationBlockId } from 'KnmPackage/MainModule/constants/fieldsElementsIds';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmErpObjectResultSelectOption } from 'KnmErpPackage/MainModule/interfaces/IKnmErpObjectResultSelectOption';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import knmSharedStyles from 'KnmPackage/MainModule/styles/KnmShared.module.scss';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Section } from 'UIPackage/FormModule/components/Section';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { IKnmErpFormViolation } from '../interfaces/IKnmErpFormViolation';
import { KnmErpViolation } from './KnmErpViolation';
import styles from './KnmErpViolations.module.scss';

interface IKnmErpViolationsProps {
  disabled: boolean;
  error: string;
  knmObjectsResultsSelectOptions: IKnmErpObjectResultSelectOption[] | undefined;
  violationsNumber: number;
  watchOnly?: boolean;
  makeLegalBasesOpenButtonClickHandler: (violationIndex: number) => () => void;
}
interface IKnmErpViolationsStoreProps {
  formRef: IKnmFormRef | undefined;
}

const KnmErpViolationsWithoutStore = ({
  disabled,
  error,
  knmObjectsResultsSelectOptions,
  violationsNumber,
  watchOnly,
  makeLegalBasesOpenButtonClickHandler,
  formRef,
}: IKnmErpViolationsProps & IKnmErpViolationsStoreProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const addViolation = useCallback(() => !watchOnly && push && push('violations', { isNew: true, listId: uuidv4() }), [
    push,
    watchOnly,
  ]);
  const makeRemoveViolationHandler = (index: number) => () => !watchOnly && remove && remove('violations', index);
  const removeAllViolations = useCallback(() => formRef?.current.change('violations', undefined), [formRef]);

  return (
    <Section
      title="Список выявленных нарушений"
      type="list"
      customButton={
        <>
          <Button
            className="shared-btn-add"
            text="Добавить"
            type="button"
            variant="link"
            color="primary"
            disabled={disabled || Boolean(error) || watchOnly}
            onClick={addViolation}
          />
          {violationsNumber > 0 && (
            <Button
              className={styles.DeleteButton}
              text="Удалить все нарушения"
              type="button"
              variant="link"
              color="danger"
              disabled={watchOnly}
              onClick={removeAllViolations}
            />
          )}
        </>
      }
      id={KNM_VIOLATIONS_LIST}
    >
      {error && (
        <Row>
          <p className={knmSharedStyles.SectionErrorNotification}>{error}</p>
        </Row>
      )}

      <FieldArray name="violations">
        {({ fields }) => {
          if (!fields.length) {
            return null;
          }

          return fields.map((name, index) => {
            const fieldValue: IKnmErpFormViolation = fields.value?.[index];
            const id = getViolationBlockId(fieldValue.listId);

            return (
              <KnmErpViolation
                key={id}
                formRef={formRef}
                id={id}
                index={index}
                knmObjectsResultsSelectOptions={knmObjectsResultsSelectOptions}
                name={name}
                value={fieldValue}
                watchOnly={watchOnly}
                onLegalBasesOpenButtonClick={makeLegalBasesOpenButtonClickHandler(index)}
                onRemove={makeRemoveViolationHandler(index)}
              />
            );
          });
        }}
      </FieldArray>
    </Section>
  );
};

const mapStateToProps = (state: IRootState): IKnmErpViolationsStoreProps => ({
  formRef: getKnmFormRef(state),
});

export const KnmErpViolations = connect(mapStateToProps)(KnmErpViolationsWithoutStore);
